import * as React from 'react'
import { useNavigate } from 'react-router-dom'

import { Box, Button } from '@factorialco/deprecated-design-system'
import {
  ContractDetailDeleteContractVersionDocument,
  ContractVersionPageContractVersionFragment,
  ContractVersionsPageDocument,
  EmployeeProfilePageDocument,
} from 'generated/resources/graphql'

import { observer } from 'mobx-react'

import Modal from 'modules/core/components/Modal'
import PopoverMenu from 'modules/core/components/PopoverMenu'
import { useMutation } from 'modules/core/hooks/useMutation'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { getContractVersionSchedule } from 'modules/contracts/lib/graphqlContractVersionSchedule'

interface Props {
  employeeId: number
  selectedContractVersion: ContractVersionPageContractVersionFragment
  allContractVersions: ContractVersionPageContractVersionFragment[]
  hasContractVersionRequest: boolean
  onClickFix: () => void
  onClickDelete: () => void
}

const Menu = ({
  employeeId,
  selectedContractVersion,
  allContractVersions,
  hasContractVersionRequest,
  onClickFix,
  onClickDelete,
}: Props) => {
  const navigate = useNavigate()
  const { navigation } = useNavigation()
  const contractVersionSchedule = getContractVersionSchedule(
    selectedContractVersion,
    allContractVersions
  )
  const [showDeleteModal, setShowDeleteModal] = React.useState(false)
  const canEditContracts = !!selectedContractVersion._policies?.edit?.can
  const canDeleteContracts = !!selectedContractVersion._policies?.delete?.can

  const [deleteContractVersion, { loading: deletingContractVersion }] = useMutation(
    ContractDetailDeleteContractVersionDocument,
    {
      extractorFn: (data) => data.contractsMutations.deleteContractsContractVersion,
      refetchQueries: [EmployeeProfilePageDocument, ContractVersionsPageDocument],
      awaitRefetchQueries: true,
    }
  )

  const onDelete = async () => {
    if (!selectedContractVersion.id) return
    // If a contract version is pending, both delete and fix perform the same action,
    // blocking any changes to the contract versions until the contract
    // change is approved or rejected.
    const result = await deleteContractVersion({
      variables: {
        id: selectedContractVersion.id,
      },
    })

    const handled = handleResult(result, (_errors) => {})
    if (handled.success) {
      navigate(navigation.employees.id.fullPath({ id: employeeId }))
      app.setFlash({
        type: 'correct',
        message: i18n.t('contract_versions.modals.delete.success'),
        time: 2000,
      })
      track(trackingEvents.employee.contracts.deleteContractConditions, {
        contract_status: contractVersionSchedule,
      })
    } else {
      app.setFlash({
        type: 'wrong',
        message: i18n.t('contract_versions.modals.delete.error'),
        time: 2000,
      })
    }
  }

  const onCancelDelete = () => {
    setShowDeleteModal(false)
  }

  const onConfirmClickDelete = () => {
    setShowDeleteModal(false)
    onDelete()
  }

  const onDeleteClick = () => {
    if (!hasContractVersionRequest) {
      setShowDeleteModal(true)
    }
    onClickDelete()
  }

  const onFix = () => {
    track(trackingEvents.employee.contracts.fixContractStart, {
      contract_status: contractVersionSchedule,
    })
    onClickFix()
  }

  return (
    <>
      <Box role="menu">
        <PopoverMenu type="list">
          <PopoverMenu.Trigger>
            <Button.Decorative type="secondary" iconName="ellipsis" />
          </PopoverMenu.Trigger>
          <PopoverMenu.Content side="bottom" align="end">
            {canEditContracts && (
              <Box className="employee_profile_contracts_tab_fix_conditions_button">
                <PopoverMenu.TouchableOption
                  title={i18n.t('contract_versions.header.fix_conditions')}
                  onClick={() => onFix()}
                />
              </Box>
            )}
            {canDeleteContracts && (
              <PopoverMenu.TouchableOption
                title={i18n.t('contract_versions.navigator.cards.delete_version')}
                onClick={() => onDeleteClick()}
                danger
              />
            )}
          </PopoverMenu.Content>
        </PopoverMenu>
      </Box>
      {showDeleteModal && (
        <Modal
          title={i18n.t('contract_versions.modals.delete.title')}
          description={i18n.t('contract_versions.modals.delete.description')}
          illustration="delete"
          onClose={() => onCancelDelete()}
          onSubmit={() => onConfirmClickDelete()}
          footer={
            <>
              <Button type="secondary" onClick={() => onCancelDelete()}>
                {i18n.t('contract_versions.modals.delete.cancel')}
              </Button>
              <Button
                type="negative"
                submit
                disabled={deletingContractVersion}
                loading={deletingContractVersion}
              >
                {i18n.t('contract_versions.modals.delete.delete')}
              </Button>
            </>
          }
        />
      )}
    </>
  )
}

export default observer(Menu)
